import React, { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import api, { API } from "../lib/api";
import { toast } from "sonner";
import { Activity, Clock, Gauge, AlertTriangle, RefreshCw, Download } from "lucide-react";

const BAND_KEYS = ["Normal", "Mild", "Moderate", "High"];
const BAND_TONE = {
  Normal: "bg-primary/25 text-primary",
  Mild: "bg-secondary/40 text-primary",
  Moderate: "bg-[hsl(var(--chart-4)/0.35)] text-foreground",
  High: "bg-destructive/25 text-destructive",
};
const BAND_ROW_TONE = {
  Normal: "border-primary/30 bg-primary/5",
  Mild: "border-secondary bg-secondary/15",
  Moderate: "border-[hsl(var(--chart-4))] bg-[hsl(var(--chart-4)/0.1)]",
  High: "border-destructive/40 bg-destructive/5",
};

function cellTone(row) {
  if (!row || row.total === 0) return "bg-muted/40 text-muted-foreground";
  const pct = (row.High + row.Moderate * 0.5) / row.total;
  if (pct >= 0.4) return "bg-destructive/25 text-destructive";
  if (pct >= 0.2) return "bg-[hsl(var(--chart-4)/0.3)] text-foreground";
  if (pct > 0) return "bg-secondary/40 text-primary";
  return "bg-primary/10 text-primary";
}

function Sparkline({ points, height = 44, testid }) {
  if (!points || points.length === 0) return null;
  const w = 320;
  const max = Math.max(1, ...points.map((p) => p.count));
  const stepX = w / (points.length - 1 || 1);
  const path = points.map((p, i) =>
    `${i === 0 ? "M" : "L"}${(i * stepX).toFixed(1)},${(height - (p.count / max) * (height - 6) - 3).toFixed(1)}`
  ).join(" ");
  const bars = points.map((p, i) => {
    const x = i * stepX - 1.5;
    const h = (p.count / max) * (height - 6);
    const y = height - h - 3;
    return <rect key={i} x={x} y={y} width={3} height={Math.max(1, h)} fill="currentColor" opacity={0.35}>
      <title>{p.date} · {p.count}</title>
    </rect>;
  });
  return (
    <svg viewBox={`0 0 ${w} ${height}`} className="w-full text-primary" data-testid={testid}>
      {bars}
      <path d={path} fill="none" stroke="currentColor" strokeWidth={1.5} />
    </svg>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

async function downloadCsv(path, params, fallbackName) {
  try {
    const token = localStorage.getItem("mano_jwt");
    const url = new URL(`${API}${path}`);
    Object.entries(params || {}).forEach(([k, v]) => v != null && url.searchParams.set(k, v));
    const resp = await fetch(url.toString(), {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const disposition = resp.headers.get("content-disposition") || "";
    const match = /filename="?([^"]+)"?/.exec(disposition);
    const filename = (match && match[1]) || fallbackName;
    const blob = await resp.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 1500);
    toast.success(`Exported ${filename}`);
  } catch (e) {
    toast.error("CSV export failed");
  }
}

function DrilldownDialog({ open, onOpenChange, cohortId, instrumentId, cohortName, instrumentTitle }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !cohortId || !instrumentId) return;
    setData(null);
    setLoading(true);
    api.get("/admin/analytics/drilldown", { params: { cohort_id: cohortId, instrument_id: instrumentId } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load drill-down"))
      .finally(() => setLoading(false));
  }, [open, cohortId, instrumentId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="drilldown-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle className="font-display">
                {cohortName || "Cohort"} <span className="text-muted-foreground">·</span> {instrumentTitle || instrumentId}
              </DialogTitle>
              <DialogDescription>
                Latest response per respondent in this cohort for this instrument.
              </DialogDescription>
            </div>
            <Button
              size="sm" variant="outline" className="rounded-full shrink-0"
              disabled={!data || data.responses.length === 0}
              onClick={() => downloadCsv(
                "/admin/analytics/drilldown.csv",
                { cohort_id: cohortId, instrument_id: instrumentId },
                `manodayam-drilldown-${cohortId}-${instrumentId}.csv`,
              )}
              data-testid="drilldown-export-csv-btn"
            >
              <Download size={12} /> Export CSV
            </Button>
          </div>
        </DialogHeader>

        {loading && <div className="text-sm text-muted-foreground p-4">Loading…</div>}

        {data && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
              {BAND_KEYS.map((b) => (
                <div key={b} className={"rounded-lg border p-3 text-center " + BAND_ROW_TONE[b]}>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{b}</div>
                  <div className="font-display text-xl mt-1" data-testid={`drilldown-count-${b}`}>{data.counts[b] || 0}</div>
                </div>
              ))}
              <div className="rounded-lg border border-border/60 bg-muted/40 p-3 text-center">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">In cohort</div>
                <div className="font-display text-xl mt-1" data-testid="drilldown-cohort-size">{data.respondent_count_in_cohort}</div>
              </div>
            </div>

            {data.responses.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-8">
                No responses recorded yet for this cohort × instrument.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="drilldown-table">
                  <thead className="bg-muted/50 text-left">
                    <tr>
                      <th className="p-2 font-medium">Respondent</th>
                      <th className="p-2 font-medium">Band</th>
                      <th className="p-2 font-medium">Score</th>
                      <th className="p-2 font-medium">Submitted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.responses.map((r) => (
                      <tr key={r.user_id} className="border-t border-border/60">
                        <td className="p-2">
                          <div>{r.name}</div>
                          <div className="text-xs text-muted-foreground">{r.email}</div>
                        </td>
                        <td className="p-2">
                          <Badge className={"border " + (BAND_TONE[r.band] || "")}>{r.band || "—"}</Badge>
                          {r.label && <div className="text-[11px] text-muted-foreground mt-0.5">{r.label}</div>}
                        </td>
                        <td className="p-2 font-display text-base">{r.total ?? "—"}</td>
                        <td className="p-2 text-xs text-muted-foreground whitespace-nowrap">
                          {(r.submitted_at || "").replace("T", " ").slice(0, 16)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tick, setTick] = useState(0);
  const [drill, setDrill] = useState({ open: false, cohortId: null, instrumentId: null, cohortName: "", instrumentTitle: "" });

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const r = await api.get("/admin/analytics");
      setData(r.data);
    } catch {
      toast.error("Could not load analytics");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 30000);
    return () => clearInterval(t);
  }, []);

  if (loading) return <div className="text-muted-foreground text-sm">Loading analytics…</div>;
  if (!data) return null;

  const { heatmap, sla, high_severity_trend, generated_at } = data;
  const trendTotal = high_severity_trend.reduce((a, b) => a + b.count, 0);

  const openDrill = (c, i) => setDrill({
    open: true, cohortId: c.cohort_id, instrumentId: i.instrument_id,
    cohortName: c.name, instrumentTitle: i.title || i.instrument_id,
  });

  return (
    <div className="space-y-8" data-testid="analytics-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-muted-foreground" data-testid="analytics-updated">
          Updated {timeAgo(generated_at)} · <span className="opacity-70">{(generated_at || "").replace("T", " ").slice(0, 19)} UTC</span>
          <span className="sr-only">{tick}</span>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm" variant="outline" className="rounded-full"
                data-testid="analytics-export-csv-btn"
              >
                <Download size={12} /> Export CSV
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72" data-testid="analytics-export-menu">
              <DropdownMenuLabel className="text-xs">Choose export scope</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => downloadCsv(
                  "/admin/analytics/snapshot.csv", null,
                  "manodayam-analytics-snapshot.csv",
                )}
                data-testid="export-snapshot-item"
                className="flex flex-col items-start gap-0.5"
              >
                <span className="font-medium">Full analytics snapshot</span>
                <span className="text-[11px] text-muted-foreground">SLA + trend + cohort × instrument matrix</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled
                data-testid="export-drilldown-hint"
                className="flex flex-col items-start gap-0.5"
              >
                <span className="font-medium">Current drill-down view</span>
                <span className="text-[11px] text-muted-foreground">Open any heatmap cell, then Export CSV from the dialog.</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            size="sm" variant="outline" onClick={() => load(true)} disabled={refreshing}
            className="rounded-full" data-testid="analytics-refresh-btn"
          >
            <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="mano-card"><CardContent className="p-5">
          <div className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-secondary/40 text-primary"><Clock size={16} /></div>
          <div className="text-xs text-muted-foreground mt-3 uppercase tracking-wider">Cases finalized</div>
          <div className="font-display text-2xl mt-1" data-testid="sla-finalized">{sla.finalized_count}</div>
        </CardContent></Card>
        <Card className="mano-card"><CardContent className="p-5">
          <div className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-secondary/40 text-primary"><Gauge size={16} /></div>
          <div className="text-xs text-muted-foreground mt-3 uppercase tracking-wider">Median turnaround</div>
          <div className="font-display text-2xl mt-1" data-testid="sla-median">
            {sla.median_hours != null ? `${sla.median_hours}h` : "—"}
          </div>
        </CardContent></Card>
        <Card className="mano-card"><CardContent className="p-5">
          <div className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-secondary/40 text-primary"><Activity size={16} /></div>
          <div className="text-xs text-muted-foreground mt-3 uppercase tracking-wider">p95 turnaround</div>
          <div className="font-display text-2xl mt-1" data-testid="sla-p95">
            {sla.p95_hours != null ? `${sla.p95_hours}h` : "—"}
          </div>
        </CardContent></Card>
        <Card className="mano-card"><CardContent className="p-5">
          <div className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-destructive/20 text-destructive"><AlertTriangle size={16} /></div>
          <div className="text-xs text-muted-foreground mt-3 uppercase tracking-wider">High-severity (30d)</div>
          <div className="font-display text-2xl mt-1" data-testid="stat-high-severity-30d">{trendTotal}</div>
        </CardContent></Card>
      </div>

      <Card className="mano-card"><CardContent className="p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold text-lg">High-severity case trend (30 days · UTC)</h3>
          <span className="text-xs text-muted-foreground">Daily count · newest right</span>
        </div>
        <Sparkline points={high_severity_trend} testid="analytics-trend-svg" />
      </CardContent></Card>

      <Card className="mano-card"><CardContent className="p-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="font-display font-semibold text-lg">Cohort × Instrument · Band distribution</h3>
            <p className="text-xs text-muted-foreground mt-1">Click any cell for a per-respondent breakdown.</p>
          </div>
          <div className="flex items-center gap-2">
            {BAND_KEYS.map((b) => (
              <span key={b} className={"text-[10px] rounded-full px-2 py-0.5 " + BAND_TONE[b]}>{b}</span>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto" data-testid="analytics-heatmap">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left">
                <th className="p-2 font-medium text-muted-foreground text-xs uppercase tracking-wider">Cohort</th>
                {heatmap.instruments.map((i) => (
                  <th key={i.instrument_id} className="p-2 font-medium text-xs whitespace-nowrap">{i.instrument_id}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {heatmap.cohorts.map((c) => (
                <tr key={c.cohort_id} className="border-t border-border/60">
                  <td className="p-2 font-medium whitespace-nowrap">
                    {c.name}
                    {c.cohort_id === "__none__" && (
                      <Badge variant="outline" className="ml-2 text-[10px]">no cohort</Badge>
                    )}
                  </td>
                  {heatmap.instruments.map((i) => {
                    const row = heatmap.matrix?.[c.cohort_id]?.[i.instrument_id];
                    const tone = cellTone(row);
                    const clickable = row && row.total > 0;
                    return (
                      <td key={i.instrument_id} className="p-2">
                        <button
                          type="button"
                          disabled={!clickable}
                          onClick={() => openDrill(c, i)}
                          className={
                            "w-full rounded-md px-2 py-1.5 text-center leading-tight min-w-[86px] transition-colors " +
                            tone + (clickable ? " hover:brightness-95 cursor-pointer" : " cursor-default")
                          }
                          data-testid={`heatmap-${c.cohort_id}-${i.instrument_id}`}
                          title={row ? `Normal ${row.Normal} · Mild ${row.Mild} · Moderate ${row.Moderate} · High ${row.High}` : "no data"}
                        >
                          {row && row.total > 0 ? (
                            <>
                              <div className="font-display text-base">{row.total}</div>
                              <div className="text-[10px] opacity-80">
                                {row.High > 0 && `${row.High}H · `}{row.Moderate > 0 && `${row.Moderate}M · `}{row.Mild > 0 && `${row.Mild}Md · `}{row.Normal > 0 && `${row.Normal}N`}
                              </div>
                            </>
                          ) : (
                            <div className="text-xs opacity-60">—</div>
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground mt-4">
          Cell colour reflects the share of Moderate + High bands (weight: H=1, M=0.5). Numbers show total latest responses per respondent. Days are UTC.
        </p>
      </CardContent></Card>

      <DrilldownDialog
        open={drill.open}
        onOpenChange={(v) => setDrill((d) => ({ ...d, open: v }))}
        cohortId={drill.cohortId}
        instrumentId={drill.instrumentId}
        cohortName={drill.cohortName}
        instrumentTitle={drill.instrumentTitle}
      />
    </div>
  );
}
